"use client";

import classNames from "classnames";
import {
  Children,
  KeyboardEvent,
  ReactNode,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import styles from "./Tabs.module.scss";
import { TabsItemTypes, TabsSize, TabsTypes, TabsVariant } from "./Tabs.types";

type TabsContextType = {
  activeValue: string;
  setActiveValue: (value: string) => void;
  variant: TabsVariant;
  size: TabsSize;
  fullWidth: boolean;
  baseId: string;
  values: string[];
};

const TabsContext = createContext<TabsContextType | null>(null);

const getTabId = (baseId: string, value: string) => `${baseId}-tab-${value}`;

const Tabs = ({
  children,
  className = "",
  value,
  defaultValue,
  onChange,
  variant = "secondary",
  size = "medium",
  fullWidth = false,
}: TabsTypes) => {
  const baseId = useId();

  const values = useMemo(() => {
    return Children.toArray(children).flatMap((child) => {
      if (!isValidElement<TabsItemTypes>(child)) return [];
      return child.props.value ? [child.props.value] : [];
    });
  }, [children]);

  const getInitialValue = () => value ?? defaultValue ?? values[0] ?? "";

  const [internalValue, setInternalValue] = useState(getInitialValue);

  useEffect(() => {
    if (value === undefined) return;
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    const currentValue = value ?? internalValue;

    if (values.includes(currentValue)) return;

    const nextValue = value ?? defaultValue ?? values[0] ?? "";

    setInternalValue(nextValue);
  }, [defaultValue, internalValue, value, values]);

  const activeValue = value ?? internalValue;

  const setActiveValue = (nextValue: string) => {
    if (value === undefined) setInternalValue(nextValue);
    onChange?.(nextValue);
  };

  return (
    <TabsContext.Provider
      value={{
        activeValue,
        setActiveValue,
        variant,
        size,
        fullWidth,
        baseId,
        values,
      }}
    >
      <div
        className={classNames(
          styles["comp"],
          styles[`comp--variant-${variant}`],
          styles[`comp--size-${size}`],
          fullWidth && styles["comp--full-width"],
          className
        )}
        role="tablist"
        aria-orientation="horizontal"
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

const TabsItem = ({
  value,
  children,
  className = "",
  disabled = false,
  startContent = null,
  endContent = null,
  onKeyDown,
}: TabsItemTypes) => {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error("TabsItem must be used within Tabs.");
  }

  const {
    activeValue,
    setActiveValue,
    variant,
    size,
    fullWidth,
    baseId,
    values,
  } = context;

  const isActive = activeValue === value;

  const moveFocus = (
    event: KeyboardEvent<HTMLButtonElement>,
    direction: "next" | "previous" | "first" | "last"
  ) => {
    const tabs = Array.from(
      event.currentTarget
        .closest('[role="tablist"]')
        ?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)') ?? []
    );

    if (!tabs.length) return;

    const currentIndex = tabs.findIndex((tab) => tab === event.currentTarget);

    const nextIndex =
      direction === "first"
        ? 0
        : direction === "last"
        ? tabs.length - 1
        : direction === "next"
        ? (currentIndex + 1) % tabs.length
        : (currentIndex - 1 + tabs.length) % tabs.length;

    tabs[nextIndex]?.focus();
    tabs[nextIndex]?.click();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);

    if (event.defaultPrevented) return;

    if (event.key === "ArrowRight") {
      event.preventDefault();
      moveFocus(event, "next");
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveFocus(event, "previous");
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      moveFocus(event, "first");
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      moveFocus(event, "last");
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!disabled) setActiveValue(value);
    }
  };

  return (
    <button
      id={getTabId(baseId, value)}
      type="button"
      role="tab"
      tabIndex={isActive ? 0 : -1}
      aria-selected={isActive}
      disabled={disabled}
      data-tab-value={value}
      className={classNames(
        styles["comp__item"],
        styles[`comp__item--variant-${variant}`],
        styles[`comp__item--size-${size}`],
        fullWidth && styles["comp__item--full-width"],
        isActive && styles["comp__item--active"],
        disabled && styles["comp__item--disabled"],
        className
      )}
      onClick={() => setActiveValue(value)}
      onKeyDown={handleKeyDown}
    >
      {startContent && (
        <span className={styles["comp__item__content"]}>{startContent}</span>
      )}
      <span className={styles["comp__item__label"]}>{children}</span>
      {endContent && (
        <span className={styles["comp__item__content"]}>{endContent}</span>
      )}
    </button>
  );
};

export { Tabs, TabsItem };
